import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

const BASE = `${API_BASE_URL}/api/attendance-module`;

// The server trusts these headers (same convention as routes/aiPaper.js).
const headers = (user) => ({
  'x-user-role': user.role,
  'x-user-name': user.name || user.email || '',
  'x-user-class': user.class || ''
});

export const fetchClasses = (user) =>
  axios.get(`${BASE}/classes`, { headers: headers(user) }).then((r) => r.data);

export const fetchRoster = (user, className, from, to) =>
  axios.get(`${BASE}/roster`, { headers: headers(user), params: { class: className, from, to } }).then((r) => r.data);

export const saveBulk = (user, className, entries) =>
  axios.put(`${BASE}/bulk`, { class: className, entries }, { headers: headers(user) }).then((r) => r.data);

// Quick-Mark dashboard: every class + students + saved absentees for a date (one call).
export const fetchBulkRoster = (user, date) =>
  axios.get(`${BASE}/bulk-roster`, { headers: headers(user), params: { date } }).then((r) => r.data);

// Whole school in one transaction: [{ classId, date, absentStudentIds[] }]
export const bulkSaveAttendance = (user, payload) =>
  axios.post(`${API_BASE_URL}/api/attendance/bulk-save`, payload, { headers: headers(user) }).then((r) => r.data);

export const saveHoliday = (user, payload) =>
  axios.post(`${BASE}/holidays`, payload, { headers: headers(user) }).then((r) => r.data);

export const removeHoliday = (user, payload) =>
  axios.delete(`${BASE}/holidays`, { headers: headers(user), data: payload }).then((r) => r.data);

export const fetchWhatsAppLinks = (user) =>
  axios.get(`${BASE}/whatsapp`, { headers: headers(user) }).then((r) => r.data);

export const saveWhatsAppLink = (user, className, groupLink) =>
  axios.put(`${BASE}/whatsapp/${encodeURIComponent(className)}`, { groupLink }, { headers: headers(user) }).then((r) => r.data);

export const saveTemplate = (user, className, messageTemplate) =>
  axios.put(`${BASE}/whatsapp/${encodeURIComponent(className)}/template`, { messageTemplate }, { headers: headers(user) }).then((r) => r.data);

const WA = `${BASE}/whatsapp`;

export const fetchTemplates = (user) =>
  axios.get(`${WA}/templates`, { headers: headers(user) }).then((r) => r.data); // { variables, templates }

export const createTemplate = (user, name, body) =>
  axios.post(`${WA}/templates`, { name, body }, { headers: headers(user) }).then((r) => r.data);

export const updateTemplate = (user, id, name, body) =>
  axios.put(`${WA}/templates/${id}`, { name, body }, { headers: headers(user) }).then((r) => r.data);

export const deleteTemplate = (user, id) =>
  axios.delete(`${WA}/templates/${id}`, { headers: headers(user) }).then((r) => r.data);

export const fetchTemplateMessage = (user, className, date, templateId) =>
  axios.get(`${WA}/${encodeURIComponent(className)}/message`, { headers: headers(user), params: { date, templateId } }).then((r) => r.data);

export const previewTemplate = (user, className, date, body) =>
  axios.post(`${WA}/${encodeURIComponent(className)}/message`, { date, body }, { headers: headers(user) }).then((r) => r.data);

// Returns a PNG Blob. Errors come back as JSON, so decode them for the caller.
// Pass templateId (system/custom) or an unsaved draft `body`.
// Requests base64 JSON (reliable in Android WebView) and converts it back to a PNG Blob.
export const base64ToBlob = (base64, type = 'image/png') => {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
};

export const fetchReportImage = async (user, className, date, templateId, body) => {
  const r = await axios.post(`${API_BASE_URL}/api/attendance/generate-image`, { className, date, templateId, body, format: 'base64' }, { headers: headers(user) });
  return base64ToBlob(r.data.base64, r.data.mimeType);
};

// Text to WhatsApp. whatsapp:// opens the app directly on Android; wa.me is the universal fallback.
export const openWhatsAppText = (text, phone = '') => {
  const q = encodeURIComponent(text);
  const p = String(phone).replace(/[^0-9]/g, '');
  const web = `https://wa.me/${p}?text=${q}`;
  if (/Android/i.test(navigator.userAgent)) {
    window.location.href = `whatsapp://send?${p ? `phone=${p}&` : ''}text=${q}`;
    setTimeout(() => { if (!document.hidden) window.location.href = web; }, 1200);
  } else {
    window.open(web, '_blank');
  }
};

export const fetchReport = (user, className, date) =>
  axios.get(`${BASE}/whatsapp/${encodeURIComponent(className)}/report`, { headers: headers(user), params: { date } }).then((r) => r.data);

// â”€â”€ date helpers (local time, YYYY-MM-DD) â”€â”€
export const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const todayISO = () => toISO(new Date());
export const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return toISO(d); };
export const isSunday = (iso) => new Date(`${iso}T00:00:00`).getDay() === 0;
export const monthRange = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return [toISO(new Date(d.getFullYear(), d.getMonth(), 1)), toISO(new Date(d.getFullYear(), d.getMonth() + 1, 0))];
};
export const weekRange = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const mondayOffset = (d.getDay() + 6) % 7;
  const start = addDays(iso, -mondayOffset);
  return [start, addDays(start, 5)];
};
export const datesInRange = (from, to) => {
  const out = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
};
export const academicYearOf = (iso) => {
  const y = Number(iso.slice(0, 4)); const m = Number(iso.slice(5, 7));
  const s = m >= 4 ? y : y - 1;
  return `${s}-${String((s + 1) % 100).padStart(2, '0')}`;
};
